import React, { useState, useEffect, FC, useMemo } from "react";
import FadeIn from "react-fade-in";
import { FiZap, FiFileText, FiSearch, FiX } from "react-icons/fi";
import { BiFileBlank } from "react-icons/bi";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ScatterController,
} from "chart.js";
import { Line, Bar, Scatter, Chart } from "react-chartjs-2";
import Modal from "../../../atom/modal";
import Table from "../../../atom/table";
import Lottiefy from "../../../atom/lottie/lottie";
import * as loaderLottie from "../../../utils/lottie/loaderLottie.json";
import {
  getTranscriptionAnalytics,
  exportRawDataTranscriptionAnalytics,
} from "../../../services/api";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ScatterController,
  Title,
  Tooltip,
  Legend,
  Filler,
);

interface TranscriptionSTTProps {
  startDate: string;
  endDate: string;
  refreshTrigger?: number;
  data?: any;
}

const TranscriptionSTT: FC<TranscriptionSTTProps> = ({
  startDate,
  endDate,
  refreshTrigger,
  data,
}) => {
  const [isRawRecordsModalOpen, setIsRawRecordsModalOpen] = useState(false);
  const [isHourlySummaryModalOpen, setIsHourlySummaryModalOpen] =
    useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [summarySearchQuery, setSummarySearchQuery] = useState("");

  const [rawRecords, setRawRecords] = useState<any[]>([]);
  const [loadingRawRecords, setLoadingRawRecords] = useState(false);
  const [hasFetchedRaw, setHasFetchedRaw] = useState(false);

  const [rawPage, setRawPage] = useState(1);
  const [totalRawRecords, setTotalRawRecords] = useState(0);
  const [maxPages, setMaxPages] = useState(1);

  const [sortColumn, setSortColumn] = useState<string>("created_at");
  const [sortDirection, setSortDirection] = useState<string>("desc");

  useEffect(() => {
    if (isRawRecordsModalOpen) {
      setRawPage(1);
      setSearchInput("");
      setSearchQuery("");
      setSortColumn("created_at");
      setSortDirection("desc");
      setTableTitles((prev) =>
        prev.map((col) => {
          if (col.key === "created_at") {
            return { ...col, sort: { direction: "desc", active: true } };
          } else if (col.sort) {
            return { ...col, sort: { direction: "asc", active: false } };
          }
          return col;
        })
      );
    }
  }, [isRawRecordsModalOpen]);

  // ============================================================
  // EXPORT LOADING STATES
  // ============================================================

  // Hourly Summary exports
  const [isExportingSummaryCSV, setIsExportingSummaryCSV] = useState(false);
  const [isExportingSummaryJSON, setIsExportingSummaryJSON] = useState(false);

  // Raw Records exports
  const [isExportingRawCSV, setIsExportingRawCSV] = useState(false);
  const [isExportingRawJSON, setIsExportingRawJSON] = useState(false);

  // ============================================================
  // TABLE DEFINITIONS
  // ============================================================

  const [tableTitles, setTableTitles] = useState<any[]>([
    {
      title: "Job Id",
      key: "id",
      class: "w-[30%] font-medium text-gray-700",
      sort: { direction: "asc", active: false },
      hide: {
        status: true,
        default: false,
      },
    },
    {
      title: "Created At",
      key: "created_at",
      class: "w-[25%] font-medium text-gray-600",
      sort: { direction: "desc", active: true },
      hide: {
        status: true,
        default: true,
      },
    },
    {
      title: "Audio Duration",
      key: "audio_duration",
      class: "w-[15%] font-medium text-gray-600",
      sort: { direction: "asc", active: false },
      hide: {
        status: true,
        default: false,
      },
    },
    {
      title: "Queue Wait",
      key: "stt_queue_wait",
      class: "w-[10%] font-medium text-gray-600",
      sort: { direction: "asc", active: false },
      hide: {
        status: true,
        default: false,
      },
    },
    {
      title: "Process Time",
      key: "stt_processing_time",
      class: "w-[10%] font-medium text-gray-600",
      sort: { direction: "asc", active: false },
      hide: {
        status: true,
        default: false,
      },
    },
    {
      title: "Status",
      key: "status",
      class: "w-[10%]",
      sort: { direction: "asc", active: false },
      hide: {
        status: true,
        default: false,
      },
    },
  ]);

  const onHandleSort = (node: any, orderBy: string) => {
    setSortColumn(node.key);
    setSortDirection(orderBy.toLowerCase());
  };

  const [summaryTitles, setSummaryTitles] = useState<any[]>([
    {
      title: "Hour",
      key: "hour",
      class: "w-[12%] font-bold text-gray-700",
      sort: { direction: "asc", active: true },
    },
    {
      title: "Req Volume",
      key: "req_volume",
      class: "w-[13%] font-medium text-gray-600",
      sort: { direction: "asc", active: false },
    },
    {
      title: "Processed",
      key: "processed",
      class: "w-[13%] font-medium text-gray-600",
      sort: { direction: "asc", active: false },
    },
    {
      title: "Audio In (Min)",
      key: "audio_in",
      class: "w-[17%] font-medium text-gray-600",
      sort: { direction: "asc", active: false },
    },
    {
      title: "Audio Out (Min)",
      key: "audio_out",
      class: "w-[17%] font-medium text-gray-600",
      sort: { direction: "asc", active: false },
    },
    {
      title: "Avg Queue",
      key: "avg_queue",
      class: "w-[14%] font-medium text-gray-600",
      sort: { direction: "asc", active: false },
    },
    {
      title: "Avg Process",
      key: "avg_process",
      class: "w-[14%] font-medium text-gray-600",
      sort: { direction: "asc", active: false },
    },
  ]);

  const [summarySortColumn, setSummarySortColumn] = useState<string>("hour");
  const [summarySortDirection, setSummarySortDirection] = useState<string>("asc");

  const onHandleSummarySort = (node: any, orderBy: string) => {
    setSummarySortColumn(node.key);
    setSummarySortDirection(orderBy.toLowerCase());
  };

  useEffect(() => {
    if (isHourlySummaryModalOpen) {
      setSummarySearchQuery("");
      setSummarySortColumn("hour");
      setSummarySortDirection("asc");
      setSummaryTitles((prev) =>
        prev.map((col) => {
          if (col.key === "hour") {
            return { ...col, sort: { direction: "asc", active: true } };
          } else if (col.sort) {
            return { ...col, sort: { direction: "asc", active: false } };
          }
          return col;
        })
      );
    }
  }, [isHourlySummaryModalOpen]);

  // ============================================================
  // HOURLY SUMMARY CSV EXPORT
  // ============================================================

  const handleExportCSV = () => {
    if (isExportingSummaryCSV) return;

    setIsExportingSummaryCSV(true);

    try {
      const headers = summaryTitles.map((t: any) => t.title);

      const keys = summaryTitles.map((t: any) => t.key);

      const csvRows = [headers.join(",")];

      const filteredSource = summarySource.filter((row: any) => {
        const hrText = String(row.hour || "");

        return hrText.toLowerCase().includes(summarySearchQuery.toLowerCase());
      });

      for (const row of filteredSource) {
        const values = keys.map((key: any) => {
          let val = row[key];

          if (val === undefined || val === null) {
            val = "";
          }

          const escaped = String(val).replace(/"/g, '""');

          return `"${escaped}"`;
        });

        csvRows.push(values.join(","));
      }

      const blob = new Blob([csvRows.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.setAttribute("href", url);

      link.setAttribute("download", `STT_Hourly_Summary_${startDate}.csv`);

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting hourly summary CSV:", err);
    } finally {
      setIsExportingSummaryCSV(false);
    }
  };

  // ============================================================
  // HOURLY SUMMARY JSON EXPORT
  // ============================================================

  const handleExportJSON = () => {
    if (isExportingSummaryJSON) return;

    setIsExportingSummaryJSON(true);

    try {
      const filteredSource = summarySource.filter((row: any) => {
        const hrText = String(row.hour || "");

        return hrText.toLowerCase().includes(summarySearchQuery.toLowerCase());
      });

      const jsonRows = filteredSource.map((row: any) => {
        const cleanRow: any = {};

        summaryTitles.forEach((t: any) => {
          cleanRow[t.title] = row[t.key];
        });

        return cleanRow;
      });

      const blob = new Blob([JSON.stringify(jsonRows, null, 2)], {
        type: "application/json;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.setAttribute("href", url);

      link.setAttribute("download", `STT_Hourly_Summary_${startDate}.json`);

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting hourly summary JSON:", err);
    } finally {
      setIsExportingSummaryJSON(false);
    }
  };

  // ============================================================
  // RAW RECORDS CSV EXPORT
  // ============================================================

  const handleExportRawCSV = async () => {
    if (isExportingRawCSV) return;

    setIsExportingRawCSV(true);

    try {
      let systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (systemTimeZone === "Asia/Calcutta") {
        systemTimeZone = "Asia/Kolkata";
      }
      const res = await exportRawDataTranscriptionAnalytics(
        startDate,
        "csv",
        sortColumn,
        sortDirection,
        systemTimeZone,
        "stt"
      );

      if (res?.success && res.data) {
        let csvContent = "";

        if (typeof res.data === "string") {
          csvContent = res.data;
        } else {
          csvContent = JSON.stringify(res.data);
        }

        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });

        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.setAttribute("href", url);

        link.setAttribute("download", `STT_Raw_Records_${startDate}.csv`);

        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);

        URL.revokeObjectURL(url);
      } else {
        console.error("Export raw CSV failed:", res);
      }
    } catch (err) {
      console.error("Error exporting raw CSV:", err);
    } finally {
      setIsExportingRawCSV(false);
    }
  };

  // ============================================================
  // RAW RECORDS JSON EXPORT
  // ============================================================

  const handleExportRawJSON = async () => {
    if (isExportingRawJSON) return;

    setIsExportingRawJSON(true);

    try {
      let systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (systemTimeZone === "Asia/Calcutta") {
        systemTimeZone = "Asia/Kolkata";
      }
      const res = await exportRawDataTranscriptionAnalytics(
        startDate,
        "json",
        sortColumn,
        sortDirection,
        systemTimeZone,
        "stt"
      );

      if (res?.success && res.data) {
        const jsonContent =
          typeof res.data === "string"
            ? res.data
            : JSON.stringify(res.data, null, 2);

        const blob = new Blob([jsonContent], {
          type: "application/json;charset=utf-8;",
        });

        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.setAttribute("href", url);

        link.setAttribute("download", `STT_Raw_Records_${startDate}.json`);

        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);

        URL.revokeObjectURL(url);
      } else {
        console.error("Export raw JSON failed:", res);
      }
    } catch (err) {
      console.error("Error exporting raw JSON:", err);
    } finally {
      setIsExportingRawJSON(false);
    }
  };

  // ============================================================
  // FETCH RAW RECORDS
  // ============================================================

  useEffect(() => {
    if (isRawRecordsModalOpen) {
      const fetchRaw = async () => {
        setLoadingRawRecords(true);

        try {
          const res = await getTranscriptionAnalytics(
            "stt",
            startDate,
            rawPage,
            500,
            searchQuery ? searchQuery : undefined,
            sortDirection ? sortDirection : undefined,
            sortColumn ? sortColumn : undefined,
          );

          const resData = res?.data ?? res;

          const records = resData?.raw_metrics ?? resData?.raw_records ?? [];

          setRawRecords(records);

          const totalCount =
            resData?.raw_total ??
            resData?.record_count ??
            resData?.total ??
            records.length;

          setTotalRawRecords(totalCount);

          setMaxPages(resData?.raw_pages ?? (Math.ceil(totalCount / 500) || 1));

          setHasFetchedRaw(true);
        } catch (err) {
          console.error("Error fetching raw records:", err);
        } finally {
          setLoadingRawRecords(false);
        }
      };

      fetchRaw();
    }
  }, [isRawRecordsModalOpen, startDate, rawPage, searchQuery, sortColumn, sortDirection]);

  // ============================================================
  // FORMAT HELPERS
  // ============================================================

  const formatLocalDate = (dateStr: string) => {
    if (!dateStr) return "";

    try {
      const parsedDate =
        dateStr.includes("T") &&
        !dateStr.endsWith("Z") &&
        !dateStr.includes("+")
          ? new Date(`${dateStr}Z`)
          : new Date(dateStr);

      return isNaN(parsedDate.getTime())
        ? dateStr
        : parsedDate.toLocaleString();
    } catch (e) {
      return dateStr;
    }
  };

  const formatTimeLabel = (dtStr: string) => {
    if (!dtStr) return "";

    const parts = dtStr.split(" ");

    return parts[1] || dtStr;
  };

  const formatSecondsToMS = (secVal: number) => {
    const roundedSec = Math.round(secVal);

    const mins = Math.floor(roundedSec / 60);

    const secs = roundedSec % 60;

    const pad = (n: number) => String(n).padStart(2, "0");

    return `${pad(mins)}:${pad(secs)}`;
  };

  const formatDurationHMS = (durationVal: any) => {
    if (durationVal === undefined || durationVal === null) {
      return "00:00:00";
    }

    let seconds = 0;

    if (typeof durationVal === "number") {
      seconds = durationVal;
    } else {
      seconds = parseFloat(String(durationVal).replace(/[^\d.-]/g, "")) || 0;
    }

    const rounded = Math.round(seconds);

    const hrs = Math.floor(rounded / 3600);

    const mins = Math.floor((rounded % 3600) / 60);

    const secs = rounded % 60;

    const pad = (num: number) => String(num).padStart(2, "0");

    return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
  };

  // ============================================================
  // HOURLY METRICS
  // ============================================================

  const hourlyMetrics = data?.hourly_metrics ?? [];

  const hourlyLabels = hourlyMetrics.map((row: any) =>
    formatTimeLabel(row.time),
  );

  // ============================================================
  // RAW TABLE DATA
  // ============================================================

  const recordsSource = hasFetchedRaw
    ? rawRecords
    : (data?.raw_metrics ?? data?.raw_records ?? []);

  const formattedTableData = (recordsSource as any[])
    .map((row: any) => {
      const qWait = row.stt_queue_wait ?? row.stt_queue_wait;

      const pTime = row.stt_processing_time ?? row.stt_processing_time;

      const formatTimeVal = (val: any) => {
        if (val === undefined || val === null) {
          return "0.00s";
        }

        if (typeof val === "string" && val.endsWith("s")) {
          return val;
        }

        const num = parseFloat(String(val));

        return isNaN(num) ? String(val) : `${num.toFixed(2)}s`;
      };

      const statusText = String(row.status || "COMPLETED").toUpperCase();

      const cleanText = statusText.replace(/_/g, " ");

      let statusClass = "bg-green-50 text-green-600 border-green-200";

      let statusIcon = "✓";

      if (
        statusText === "PROCESSING" ||
        statusText === "PENDING" ||
        statusText === "IN_PROGRESS" ||
        statusText === "RUNNING"
      ) {
        statusClass = "bg-blue-50 text-blue-600 border-blue-200";

        statusIcon = "↻";
      } else if (
        statusText === "FAILED" ||
        statusText === "ERROR" ||
        statusText === "FAIL"
      ) {
        statusClass = "bg-red-50 text-red-600 border-red-200";

        statusIcon = "✗";
      }

      return {
        ...row,

        id: row.id ?? row.job_id ?? "",

        created_at: formatLocalDate(row.created_at),

        audio_duration: formatDurationHMS(row.audio_duration ?? row.duration),

        stt_queue_wait: formatTimeVal(qWait),

        stt_processing_time: formatTimeVal(pTime),

        status: (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusClass}`}
          >
            <span className="font-bold">{statusIcon}</span>

            {cleanText}
          </span>
        ),
      };
    });

  // ============================================================
  // SUMMARY DATA
  // ============================================================

  const summarySource = useMemo(() => {
    const source =
      Array.isArray(data?.hourly_metrics) && data.hourly_metrics.length > 0
        ? data.hourly_metrics.map((row: any) => {
            const hrLabel = formatTimeLabel(row.time);

            const audioInVal =
              row.total_audio_minutes ??
              (row.stt_incoming_audio_duration !== undefined
                ? row.stt_incoming_audio_duration / 60
                : row.audio_in) ??
              0;

            const audioOutVal =
              row.finished_audio_minutes ??
              (row.finished_stt_audio_duration !== undefined
                ? row.finished_stt_audio_duration / 60
                : row.audio_out) ??
              0;

            const rawQueueWait = row.avg_stt_queue_wait ?? row.avg_queue ?? 0;

            const rawProcessTime =
              row.avg_stt_processing_time ?? row.avg_process ?? 0;

            return {
              ...row,
              hour: hrLabel,
              req_volume: row.stt_incoming_jobs ?? row.jobs ?? 0,
              processed: row.finished_stt_count ?? row.processed ?? 0,
              audio_in:
                typeof audioInVal === "number"
                  ? audioInVal.toFixed(2)
                  : audioInVal,
              audio_out:
                typeof audioOutVal === "number"
                  ? audioOutVal.toFixed(2)
                  : audioOutVal,
              avg_queue:
                typeof rawQueueWait === "number"
                  ? `${rawQueueWait.toFixed(2)}s`
                  : (rawQueueWait ?? "0.00s"),
              avg_process:
                typeof rawProcessTime === "number"
                  ? `${rawProcessTime.toFixed(2)}s`
                  : (rawProcessTime ?? "0.00s"),
              _raw_audio_in: audioInVal,
              _raw_audio_out: audioOutVal,
              _raw_avg_queue: rawQueueWait,
              _raw_avg_process: rawProcessTime,
            };
          })
        : (data?.hourly_summary ?? []).map((row: any) => ({
            ...row,
            _raw_audio_in: parseFloat(row.audio_in) || 0,
            _raw_audio_out: parseFloat(row.audio_out) || 0,
            _raw_avg_queue: parseFloat(row.avg_queue) || 0,
            _raw_avg_process: parseFloat(row.avg_process) || 0,
          }));

    if (!summarySortColumn) return source;

    return [...source].sort((a: any, b: any) => {
      let valA = a[summarySortColumn];
      let valB = b[summarySortColumn];

      if (summarySortColumn === "hour") {
        valA = a.time ?? a.hour;
        valB = b.time ?? b.hour;
      } else if (summarySortColumn === "audio_in") {
        valA = a._raw_audio_in;
        valB = b._raw_audio_in;
      } else if (summarySortColumn === "audio_out") {
        valA = a._raw_audio_out;
        valB = b._raw_audio_out;
      } else if (summarySortColumn === "avg_queue") {
        valA = a._raw_avg_queue;
        valB = b._raw_avg_queue;
      } else if (summarySortColumn === "avg_process") {
        valA = a._raw_avg_process;
        valB = b._raw_avg_process;
      }

      if (valA === undefined || valA === null) valA = 0;
      if (valB === undefined || valB === null) valB = 0;

      const numA = Number(valA);
      const numB = Number(valB);
      if (!isNaN(numA) && !isNaN(numB)) {
        return summarySortDirection === "asc" ? numA - numB : numB - numA;
      }

      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();

      if (strA < strB) return summarySortDirection === "asc" ? -1 : 1;
      if (strA > strB) return summarySortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, summarySortColumn, summarySortDirection]);

  const formattedSummaryData = (summarySource as any[])
    .filter((row: any) => {
      const hrText = String(row.hour || "");

      return hrText.toLowerCase().includes(summarySearchQuery.toLowerCase());
    })
    .map((row: any) => ({
      ...row,

      hour: <span className="font-bold text-gray-800">{row.hour}</span>,
    }));

  // ============================================================
  // CHART DATA
  // ============================================================

  const incomingsttDataset = hourlyMetrics.map((row: any) => row.jobs ?? 0);

  const processedsttDataset = hourlyMetrics.map(
    (row: any) => row.finished_stt_count ?? 0,
  );

  const queueWaitsttDataset = hourlyMetrics.map(
    (row: any) => row.avg_stt_queue_wait ?? 0,
  );

  const audioArrivedMinsDataset = hourlyMetrics.map(
    (row: any) =>
      row.total_audio_minutes ?? (row.stt_incoming_audio_duration / 60 || 0),
  );

  const audioProcessedMinsDataset = hourlyMetrics.map(
    (row: any) =>
      row.finished_audio_minutes ?? (row.finished_stt_audio_duration / 60 || 0),
  );

  const machineEffortMinsDataset = hourlyMetrics.map((row: any) =>
    row.total_stt_minutes !== undefined
      ? row.total_stt_minutes / 60
      : row.actual_stt_effort / 60 || 0,
  );

  const throughputSttDataset = hourlyMetrics.map(
    (row: any) => row.finished_stt_count ?? row.jobs ?? 0,
  );

  const hasSlimScatter =
    Array.isArray(data?.slim_scatter) && data.slim_scatter.length > 0;

  const scatterPoints = hasSlimScatter
    ? data.slim_scatter.map((row: any) => ({
        x: row.audio_duration ?? 0,

        y: row.stt_processing_time ?? 0,
      }))
    : [];

  const labels = hourlyMetrics.length
    ? hourlyLabels
    : (data?.load_stt?.labels ?? []);

  // ============================================================
  // 1. LOAD VS STT PERFORMANCE
  // ============================================================

  const loadSttData = {
    labels,

    datasets: [
      {
        type: "bar" as const,

        label: "Incoming Requests",

        data: hourlyMetrics.length
          ? incomingsttDataset
          : (data?.load_stt?.incoming ?? []),

        backgroundColor: "rgba(59, 130, 246, 0.2)",

        borderRadius: 4,

        barThickness: 30,
      } as any,

      {
        type: "line" as const,

        label: "Processed STT Jobs",

        data: hourlyMetrics.length
          ? processedsttDataset
          : (data?.load_stt?.processed ?? []),

        borderColor: "#3b82f6",

        borderWidth: 2,

        pointBackgroundColor: "#3b82f6",

        pointBorderColor: "#fff",

        pointBorderWidth: 2,

        tension: 0.4,
      } as any,
    ],
  };

  // ============================================================
  // 2. WORKLOAD VS QUEUE WAIT
  // ============================================================

  const workloadQueueData = {
    labels: hourlyMetrics.length
      ? hourlyLabels
      : (data?.workload_queue?.labels ?? []),

    datasets: [
      {
        type: "bar" as const,

        label: "Incoming Workload",

        data: hourlyMetrics.length
          ? incomingsttDataset
          : (data?.workload_queue?.workload ?? []),

        backgroundColor: "rgba(99, 102, 241, 0.1)",

        borderRadius: 4,

        barThickness: 30,

        yAxisID: "y",
      } as any,

      {
        type: "line" as const,

        label: "Avg Queue Wait",

        data: hourlyMetrics.length
          ? queueWaitsttDataset
          : (data?.workload_queue?.stt_queue_wait ?? []),

        borderColor: "#6366f1",

        borderWidth: 2,

        pointBackgroundColor: "#6366f1",

        pointBorderColor: "#fff",

        pointBorderWidth: 2,

        tension: 0.4,

        yAxisID: "y1",
      } as any,
    ],
  };

  // ============================================================
  // 3. WORKLOAD VS STT SPEED
  // ============================================================

  const workloadSpeedData = {
    labels: hourlyMetrics.length
      ? hourlyLabels
      : (data?.workload_speed?.labels ?? []),

    datasets: [
      {
        type: "bar" as const,

        label: "Audio Arrived (min)",

        data: hourlyMetrics.length
          ? audioArrivedMinsDataset
          : (data?.workload_speed?.arrived ?? []),

        backgroundColor: "rgba(16, 185, 129, 0.1)",

        borderRadius: 4,

        barThickness: 30,
      } as any,

      {
        type: "line" as const,

        label: "Audio Processed (min)",

        data: hourlyMetrics.length
          ? audioProcessedMinsDataset
          : (data?.workload_speed?.processed ?? []),

        borderColor: "#10b981",

        borderWidth: 2,

        pointBackgroundColor: "#10b981",

        pointBorderColor: "#fff",

        pointBorderWidth: 2,

        tension: 0.4,
      } as any,

      {
        type: "line" as const,

        label: "STT Machine Effort (min)",

        data: hourlyMetrics.length
          ? machineEffortMinsDataset
          : (data?.workload_speed?.effort ?? []),

        borderColor: "#10b981",

        borderDash: [5, 5],

        borderWidth: 1.5,

        pointRadius: 0,

        tension: 0.4,
      } as any,
    ],
  };

  // ============================================================
  // 4. JOBS THROUGHPUT
  // ============================================================

  const throughputData = {
    labels: hourlyMetrics.length
      ? hourlyLabels
      : (data?.throughput?.labels ?? []),

    datasets: [
      {
        fill: true,

        label: "Throughput",

        data: hourlyMetrics.length
          ? throughputSttDataset
          : (data?.throughput?.data ?? []),

        borderColor: "#3b82f6",

        backgroundColor: "rgba(59, 130, 246, 0.1)",

        tension: 0.4,

        pointRadius: 0,
      },
    ],
  };

  // ============================================================
  // 5. AUDIO DURATION DISTRIBUTION
  // ============================================================

  const hasHistogram =
    Array.isArray(data?.duration_histogram) &&
    data.duration_histogram.length > 0;

  const durationLabels = hasHistogram
    ? data.duration_histogram.map(
        (row: any) =>
          `${formatSecondsToMS(row.range_start)} - ${formatSecondsToMS(
            row.range_end,
          )}`,
      )
    : (data?.duration_distribution?.labels ?? []);

  const durationDistData = {
    labels: durationLabels,

    datasets: [
      {
        label: "Audio Samples",

        data: hasHistogram
          ? data.duration_histogram.map((row: any) => row.count ?? 0)
          : (data?.duration_distribution?.data ?? []),

        backgroundColor: "#8b5cf6",

        borderRadius: 4,

        barThickness: 20,
      },
    ],
  };

  // ============================================================
  // 6. DURATION VS STT PROCESSING TIME
  // ============================================================

  const scatterData = {
    datasets: [
      {
        label: "Duration vs STT Time",

        data: hasSlimScatter
          ? scatterPoints
          : (data?.duration_vs_stt?.data ?? []),

        backgroundColor: "#0ea5e9",
      },
    ],
  };

  // ============================================================
  // CHART OPTIONS
  // ============================================================

  const chartOptions = {
    responsive: true,

    maintainAspectRatio: false,

    plugins: {
      legend: {
        position: "bottom" as const,

        labels: {
          usePointStyle: true,

          padding: 20,

          font: {
            size: 12,
          },
        },
      },

      tooltip: {
        mode: "index" as const,

        intersect: false,
      },
    },

    scales: {
      y: {
        beginAtZero: true,

        grid: {
          borderDash: [5, 5],

          color: "rgba(0, 0, 0, 0.05)",
        },
      },

      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const queueWaitChartOptions = {
    ...chartOptions,

    scales: {
      ...chartOptions.scales,

      y: {
        type: "linear" as const,

        display: true,

        position: "left" as const,

        beginAtZero: true,

        title: {
          display: true,

          text: "Workload (Jobs)",

          font: {
            size: 10,

            weight: "bold",
          },
        },

        grid: {
          borderDash: [5, 5],

          color: "rgba(0, 0, 0, 0.05)",
        },
      },

      y1: {
        type: "linear" as const,

        display: true,

        position: "right" as const,

        beginAtZero: true,

        title: {
          display: true,

          text: "Queue Wait (Seconds)",

          font: {
            size: 10,

            weight: "bold",
          },
        },

        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* ======================================================
          RAW RECORDS MODAL
      ======================================================= */}

      {isRawRecordsModalOpen && (
        <Modal
          closeEvent={() => setIsRawRecordsModalOpen(false)}
          width="w-full lg:max-w-[1450px]"
          className="rounded-3xl"
          bodyClassName="p-3 sm:p-6"
          hideClose={true}
        >
          <div className="flex flex-col gap-4 sm:gap-6 w-full overflow-hidden">
            {/* Modal Header */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 line-clamp-1">
                  STT Raw Metrics Data
                </h2>

                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  Showing all processed records for the selected period
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto mt-1 sm:mt-2">
                 {/* Search */}
                <div className="relative flex-1 sm:flex-initial">
                  <FiSearch
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={16}
                  />

                  <input
                    type="text"
                    placeholder="Search records..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setSearchQuery(searchInput);
                        setRawPage(1);
                      }
                    }}
                    className="w-full pl-10 pr-10 py-2 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 sm:min-w-[240px]"
                  />

                  {searchInput && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchInput("");
                        setSearchQuery("");
                        setRawPage(1);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <FiX size={16} />
                    </button>
                  )}
                </div>

                {/* Raw Export Buttons */}
                <div className="flex items-center justify-between sm:justify-start gap-2 bg-gray-50 p-1 rounded-xl border border-gray-100">
                  {/* CSV */}
                  <button
                    onClick={handleExportRawCSV}
                    disabled={isExportingRawCSV}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-white hover:shadow-sm rounded-lg transition-all text-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isExportingRawCSV ? (
                      <span className="w-3.5 h-3.5 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <BiFileBlank size={14} className="text-green-600" />
                    )}

                    {isExportingRawCSV ? "Exporting..." : "CSV"}
                  </button>

                  {/* JSON */}
                  <button
                    onClick={handleExportRawJSON}
                    disabled={isExportingRawJSON}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-white hover:shadow-sm rounded-lg transition-all text-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isExportingRawJSON ? (
                      <span className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <BiFileBlank size={14} className="text-amber-500" />
                    )}

                    {isExportingRawJSON ? "Exporting..." : "JSON"}
                  </button>
                </div>

                {/* Close */}
                <button
                  onClick={() => setIsRawRecordsModalOpen(false)}
                  className="hidden lg:flex p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>

            {/* Modal Table */}
            <div className="w-full border border-gray-100 rounded-2xl shadow-sm overflow-x-auto min-w-0">
              <div className="min-w-[800px] lg:min-w-0 h-[60vh] sm:h-[600px]">
                {loadingRawRecords ? (
                  <div className="flex h-full items-center justify-center">
                    <Lottiefy
                      loop={true}
                      json={loaderLottie}
                      height={100}
                      width={100}
                    />
                  </div>
                ) : (
                  <Table
                    tableTitles={tableTitles}
                    setTableTitles={setTableTitles}
                    tableData={formattedTableData}
                    onHandleSort={onHandleSort}
                    width="w-full"
                    scrollHeight="h-[60vh] sm:h-[600px]"
                    rowColorClass="bg-white"
                    resultNotFound={formattedTableData.length === 0}
                    resultNotFound_Message="No results found"
                  />
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 px-4 py-2 border-t border-gray-100 w-full">
              <p className="text-[10px] sm:text-xs text-gray-400 font-medium">
                Showing {totalRawRecords === 0 || formattedTableData.length === 0 ? 0 : (rawPage - 1) * 500 + 1} -{" "}
                {Math.min(rawPage * 500, formattedTableData.length === 0 ? 0 : totalRawRecords)} of{" "}
                {formattedTableData.length === 0 ? 0 : totalRawRecords} records
              </p>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setRawPage((prev) => Math.max(prev - 1, 1))}
                  disabled={
                    rawPage === 1 ||
                    loadingRawRecords ||
                    totalRawRecords === 0 ||
                    formattedTableData.length === 0
                  }
                  className="px-3 py-1 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Previous
                </button>

                <span className="text-xs font-semibold text-gray-600">
                  Page {totalRawRecords === 0 || formattedTableData.length === 0 ? 0 : rawPage} of {totalRawRecords === 0 || formattedTableData.length === 0 ? 0 : maxPages}
                </span>

                <button
                  onClick={() =>
                    setRawPage((prev) => Math.min(prev + 1, maxPages))
                  }
                  disabled={
                    rawPage >= maxPages ||
                    loadingRawRecords ||
                    totalRawRecords === 0 ||
                    formattedTableData.length === 0
                  }
                  className="px-3 py-1 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ======================================================
          HOURLY SUMMARY MODAL
      ======================================================= */}

      {isHourlySummaryModalOpen && (
        <Modal
          closeEvent={() => setIsHourlySummaryModalOpen(false)}
          width="w-full lg:max-w-[1450px]"
          className="rounded-3xl"
          bodyClassName="p-3 sm:p-6"
          hideClose={true}
        >
          <div className="flex flex-col gap-4 sm:gap-6 w-full overflow-hidden">
            {/* Modal Header */}
            <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 line-clamp-1">
                  STT Hourly Summary Data
                </h2>

                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  Showing all processed records for the selected period
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
                {/* Summary Export Buttons */}
                <div className="flex items-center justify-between sm:justify-start gap-2 bg-gray-50 p-1 rounded-xl border border-gray-100">
                  {/* CSV */}
                  <button
                    onClick={handleExportCSV}
                    disabled={isExportingSummaryCSV}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-white hover:shadow-sm rounded-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isExportingSummaryCSV ? (
                      <span className="w-3.5 h-3.5 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <BiFileBlank size={14} className="text-green-600" />
                    )}

                    {isExportingSummaryCSV ? "Exporting..." : "CSV"}
                  </button>

                  {/* JSON */}
                  <button
                    onClick={handleExportJSON}
                    disabled={isExportingSummaryJSON}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-white hover:shadow-sm rounded-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isExportingSummaryJSON ? (
                      <span className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <BiFileBlank size={14} className="text-amber-500" />
                    )}

                    {isExportingSummaryJSON ? "Exporting..." : "JSON"}
                  </button>
                </div>

                {/* Close */}
                <button
                  onClick={() => setIsHourlySummaryModalOpen(false)}
                  className="hidden lg:flex p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>

            {/* Summary Table */}
            <div className="w-full border border-gray-100 rounded-2xl shadow-sm overflow-x-auto min-w-0">
              <div className="min-w-[800px] lg:min-w-0">
                <Table
                  tableTitles={summaryTitles}
                  setTableTitles={setSummaryTitles}
                  tableData={formattedSummaryData}
                  onHandleSort={onHandleSummarySort}
                  width="w-full"
                  scrollHeight="max-h-[65vh] sm:max-h-[680px]"
                  rowColorClass="bg-white"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-center text-center px-4">
              <p className="text-[10px] sm:text-xs text-gray-400 font-medium">
                Showing {formattedSummaryData.length} records
              </p>
            </div>
          </div>
        </Modal>
      )}

      {/* ======================================================
          HOURLY SUMMARY / RAW RECORDS TOGGLE
      ======================================================= */}

      <div className="flex justify-end gap-2 mb-2">
        <div className="flex bg-white rounded-xl p-1 shadow-sm border border-[#ccc]">
          <button
            onClick={() => setIsHourlySummaryModalOpen(true)}
            className={`px-4 py-2 text-sm font-semibold rounded-lg flex items-center gap-2 transition-all duration-200 ${
              isHourlySummaryModalOpen
                ? "bg-indigo-600 text-white shadow-md"
                : "text-gray-500 hover:bg-indigo-50 hover:text-indigo-600"
            }`}
          >
            <FiZap
              size={14}
              className={
                isHourlySummaryModalOpen ? "text-white" : "text-indigo-600"
              }
            />
            Hourly Summary
          </button>

          <button
            onClick={() => {
              setRawPage(1);

              setIsRawRecordsModalOpen(true);
            }}
            className={`px-4 py-2 text-sm font-semibold rounded-lg flex items-center gap-2 transition-all duration-200 ${
              isRawRecordsModalOpen
                ? "bg-purple-600 text-white shadow-md"
                : "text-gray-500 hover:bg-purple-50 hover:text-purple-600"
            }`}
          >
            <FiFileText
              size={14}
              className={
                isRawRecordsModalOpen ? "text-white" : "text-purple-600"
              }
            />
            Raw Records
          </button>
        </div>
      </div>

      {/* ======================================================
          GRAPHS
      ======================================================= */}

      <div className="grid grid-cols-1 gap-6">
        {/* Row 1 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-base font-bold text-gray-800 mb-6">
            Load vs. STT Performance (Hourly)
          </h3>

          <div className="h-[300px]">
            <Chart
              type="bar"
              data={loadSttData as any}
              options={chartOptions as any}
            />
          </div>
        </div>

        {/* Row 2 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-base font-bold text-gray-800 mb-6">
            Workload Volume vs. Avg Queue Wait
          </h3>

          <div className="h-[300px]">
            <Chart
              type="bar"
              data={workloadQueueData as any}
              options={queueWaitChartOptions as any}
            />
          </div>
        </div>

        {/* Row 3 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-base font-bold text-gray-800 mb-6">
            Workload Volume vs. STT Speed (Hourly)
          </h3>

          <div className="h-[300px]">
            <Chart
              type="bar"
              data={workloadSpeedData as any}
              options={chartOptions as any}
            />
          </div>
        </div>

        {/* Row 4 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Throughput */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-base font-bold text-gray-800 mb-6">
              Jobs Throughput (Hourly)
            </h3>

            <div className="h-[300px]">
              <Line data={throughputData} options={chartOptions} />
            </div>
          </div>

          {/* Duration Distribution */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-base font-bold text-gray-800 mb-6">
              Audio Duration Distribution
            </h3>

            <div className="h-[300px]">
              <Bar data={durationDistData} options={chartOptions} />
            </div>
          </div>
        </div>

        {/* Row 5 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-base font-bold text-gray-800 mb-6">
            Duration vs STT processing Time
          </h3>

          <div className="h-[300px]">
            <Scatter
              data={scatterData}
              options={
                {
                  ...chartOptions,

                  scales: {
                    ...chartOptions.scales,

                    x: {
                      type: "linear",

                      position: "bottom",

                      title: {
                        display: true,

                        text: "Audio Duration (s)",

                        font: {
                          size: 10,
                        },
                      },

                      grid: {
                        borderDash: [5, 5],

                        color: "rgba(0, 0, 0, 0.05)",
                      },
                    },

                    y: {
                      title: {
                        display: true,

                        text: "STT Time (s)",

                        font: {
                          size: 10,
                        },
                      },

                      grid: {
                        borderDash: [5, 5],

                        color: "rgba(0, 0, 0, 0.05)",
                      },
                    },
                  },
                } as any
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranscriptionSTT;
